import * as Sentry from '@sentry/vue';


function captureMessage(
  message: string,
  captureContext?: any
): string {
  if (!Sentry.isInitialized()) {
    return
  }
  return Sentry.captureMessage(message, captureContext)
}

function captureException(exception: any, hint?: any): string {
  if (!Sentry.isInitialized()) {
    return
  }
  return Sentry.captureException(exception, hint)
}

/**
 * Shared Sentry `beforeSend` filter. Drops known-noisy, non-actionable events
 * so they don't pollute the Sentry dashboard.
 * Import and assign directly: `beforeSend: sentryBeforeSend`
 */
function sentryBeforeSend(event: any): any | null {
  const firstException = event?.exception?.values?.[0]
  const firstFrame = firstException?.stacktrace?.frames?.[0]
  const firstRawFrame = firstException?.raw_stacktrace?.frames?.[0]
  const exceptionType: string = firstException?.type || ''
  const exceptionValue: string = firstException?.value || ''
  const framePath: string = firstFrame?.abs_path || firstRawFrame?.abs_path || ''
  const frameFile: string = firstFrame?.filename || firstRawFrame?.filename || ''
  const frameContext: string = firstFrame?.context_line || firstRawFrame?.context_line || ''

  // Gate expensive breadcrumb scan to only the exception types that use it.
  const needsBreadcrumbCheck =
    exceptionType === 'ApolloError' ||
    (exceptionType === 'TypeError' && exceptionValue.includes('Load failed'))
  const breadcrumbValues: any[] = Array.isArray(event?.breadcrumbs?.values)
    ? event.breadcrumbs.values
    : []
  const hasGraphqlLoadFailureBreadcrumb =
    needsBreadcrumbCheck &&
    breadcrumbValues.some((bc: any) => {
      const url: string = bc?.data?.url || ''
      return (
        bc?.category === 'fetch' &&
        bc?.level === 'error' &&
        url.includes('/api/graphql') &&
        (bc?.data?.status_code == null || bc?.data?.status_code === 0)
      )
    })

  // Ignore browser/script-loader parse noise (HTML document parsed as JS, line 1).
  if (
    exceptionType === 'SyntaxError' &&
    exceptionValue.includes('numeric literal') &&
    firstFrame?.lineno === 1 &&
    frameContext.includes('<!DOCTYPE html')
  ) {
    return null
  }

  // Ignore old-browser parse errors from Zoho SalesIQ modern bundle (third-party script).
  if (
    exceptionType === 'SyntaxError' &&
    exceptionValue.includes("expected expression, got '.'") &&
    (framePath.includes('static.zohocdn.com/salesiq/') ||
      frameFile.includes('/salesiq/RESOURCE_BUNDLES/embedfloat/js/float~modern.'))
  ) {
    return null
  }

  // Ignore transient GraphQL transport failures on mobile Safari (fetch status 0).
  if (
    (exceptionType === 'ApolloError' || exceptionType === 'TypeError') &&
    exceptionValue.includes('Load failed') &&
    hasGraphqlLoadFailureBreadcrumb
  ) {
    return null
  }

  // Ignore non-error promise rejections from browser/extension internals.
  if (
    exceptionType === 'UnhandledRejection' &&
    exceptionValue.includes('Non-Error promise rejection captured with value: Object Not Found Matching Id') &&
    exceptionValue.includes('MethodName:update')
  ) {
    return null
  }

  // Drop generic, non-actionable noise (asset/network/transport hiccups, WKWebView bridge).
  if (
    exceptionValue.includes('webkit.messageHandlers') ||
    exceptionValue.includes('Unable to preload CSS') ||
    exceptionValue.includes('Request aborted') ||
    exceptionValue.includes('Network Error')
  ) {
    return null
  }

  // Drop expected "blocked/forbidden" session checks (ITP/WAF/cookie restrictions).
  const api: string = event?.extra?.api || ''
  if (
    api.includes('account/login/checkState') &&
    exceptionValue.includes('status code 403')
  ) {
    return null
  }

  // Drop VueUse useEventListener flatMap bug from stale production builds.
  // VueUse >=10.x fixed this by normalising the event param to array before calling flatMap.
  // Stale bundles (built before that fix) crash Footer's setup() on every page load.
  // A fresh production build eliminates the error; this filter suppresses it in the interim.
  if (
    exceptionType === 'TypeError' &&
    exceptionValue.includes('flatMap is not a function') &&
    event?.contexts?.vue?.componentName === '<Footer>'
  ) {
    return null
  }

  return event
}

export { captureMessage, captureException, sentryBeforeSend }
